/**
 * AddToCart for the ad pixels on the purchases page (Meta, Reddit, TikTok).
 *
 * The payload builders are pure so the wire shape is unit-tested. Meta's
 * event id goes in fbq's separate 4th options argument ({ eventID }), not in
 * the custom data — that's the only place Meta reads it for deduplication.
 * No email or user id goes in custom data: signed-in users are matched by the
 * hashed external_id the pixel is initialised with (facebook/_pixel.html.erb).
 *
 * The pixels are only on the page with marketing cookie consent, so each call
 * is guarded on its global existing.
 */

export interface AddToCartItem {
  priceId: string
  value: number
  currency: string
  productId: string
}

export function metaAddToCartArgs(item: AddToCartItem, now: number) {
  return [
    'track',
    'AddToCart',
    {
      value:        item.value,
      currency:     item.currency,
      content_ids:  [item.productId],
      content_type: 'product',
    },
    { eventID: `checkout-${item.priceId}-${now}` },
  ] as const
}

export function redditAddToCartArgs(item: AddToCartItem) {
  return [
    'track',
    'AddToCart',
    {
      value:     item.value,
      currency:  item.currency,
      itemCount: 1,
      products:  [{ id: item.productId }],
    },
  ] as const
}

export function tiktokAddToCartArgs(item: AddToCartItem) {
  return [
    'AddToCart',
    {
      value:    item.value,
      currency: item.currency,
      contents: [{ content_id: item.productId, content_type: 'product', quantity: 1 }],
    },
  ] as const
}

type PixelWindow = {
  fbq?: (...args: unknown[]) => void
  rdt?: (...args: unknown[]) => void
  ttq?: { track?: (...args: unknown[]) => void }
}

export function trackAddToCart(item: AddToCartItem, win: PixelWindow = window as unknown as PixelWindow, now = Date.now()) {
  if (typeof win.fbq === 'function') win.fbq(...metaAddToCartArgs(item, now))
  if (typeof win.rdt === 'function') win.rdt(...redditAddToCartArgs(item))
  if (win.ttq && typeof win.ttq.track === 'function') win.ttq.track(...tiktokAddToCartArgs(item))
}

// For the inline script in purchases/index.html.erb.
;(window as any).trackAddToCart = (item: AddToCartItem) => trackAddToCart(item)
